import { createSlice, PayloadAction } from '@reduxjs/toolkit';

/**
 * Базовая форма state для любого лениво-загружаемого списка.
 * Экспортируется, чтобы конкретные слайсы (напр. `myTests`) могли объявить
 * свой `State`-тип в `src/store/types/<feature>Types.ts` через
 * `extends LazyLoadState<NormalizedXxx>` — см. правило
 * `redux-store-state-types.mdc`.
 */
export interface LazyLoadState<T> {
    data: T[];
    isFetching: boolean;
    error: string | null;
    currentPage: number;
    lastPage: number;
    isLastPageSet: boolean;
}

const createLazyLoadSlice = <T>(name: string, initialStateOverrides: Partial<LazyLoadState<T>> = {}) => {
    const initialState: LazyLoadState<T> = {
        data: [],
        isFetching: false,
        error: null,
        currentPage: 1,
        lastPage: 1,
        isLastPageSet: false,
        ...initialStateOverrides,
    };

    return createSlice({
        name,
        initialState,
        reducers: {
            fetchDataError(state, action: PayloadAction<string>) {
                state.error = action.payload;
                state.isFetching = false;
            },
            fetchDataStart(state) {
                state.isFetching = true;
            },
            setDataLoaded(state, action: PayloadAction<T[]>) {
                state.data = Array.from(new Set([...state.data, ...action.payload])) as [];
                state.isFetching = false;
            },
            setLastPage(state, action: PayloadAction<number>) {
                state.lastPage = action.payload;
                state.isLastPageSet = true;
            },
            incrementPage(state) {
                if (!state.isFetching && state.currentPage < state.lastPage) {
                    state.currentPage += 1;
                }
            },
        },
    });
};

export default createLazyLoadSlice;
